import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './Contact.css';

const API_URL = 'https://contact-server-phi.vercel.app/api/contact';
const APP_ID = 'jagoalt-landing';

const Contact: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatusMessage('');
    if (!email.trim() || !message.trim()) {
      setStatus('error');
      setStatusMessage('Please enter your email and message.');
      return;
    }
    setStatus('sending');
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim() || undefined,
          email: email.trim(),
          message: message.trim(),
          appId: APP_ID,
          domain: typeof window !== 'undefined' ? window.location.hostname : '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setStatus('success');
        setStatusMessage("Thanks for reaching out! We'll get back to you soon.");
        setName('');
        setEmail('');
        setMessage('');
      } else {
        setStatus('error');
        setStatusMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setStatusMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <div className="contact-hero-background">
          <div className="contact-hero-particles"></div>
          <div className="gradient-orb gradient-orb-1"></div>
          <div className="gradient-orb gradient-orb-2"></div>
          <div className="gradient-orb gradient-orb-3"></div>
        </div>
        <div className="container">
          <motion.h1
            className="contact-title"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Get in Touch
          </motion.h1>
          <motion.p
            className="contact-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Have a project in mind or want to learn more about our work? We'd love to hear from you.
          </motion.p>
        </div>
      </div>

      <div className="contact-content">
        <div className="container">
          <motion.div
            className="contact-form-container"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="contact-form-wrapper">
              <h2>Send us a message</h2>
              <p className="contact-form-description">
                Tell us about your project and we'll get back to you within 1-2 business days.
              </p>

              <form className="contact-form-inline" onSubmit={handleSubmit} noValidate>
                <div className="contact-form-field">
                  <label htmlFor="contact-name">Name</label>
                  <input
                    id="contact-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </div>
                <div className="contact-form-field">
                  <label htmlFor="contact-email">Email <span className="required">*</span></label>
                  <input
                    id="contact-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                  />
                </div>
                <div className="contact-form-field">
                  <label htmlFor="contact-message">Message <span className="required">*</span></label>
                  <textarea
                    id="contact-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us about your project..."
                    required
                    rows={5}
                  />
                </div>
                <div className="contact-form-actions">
                  <button type="submit" className="contact-form-submit" disabled={status === 'sending'}>
                    {status === 'sending' ? 'Sending…' : 'Send Message'}
                  </button>
                </div>
                {statusMessage && (
                  <p className={`contact-form-status ${status}`} role="status">
                    {statusMessage}
                  </p>
                )}
              </form>
            </div>
          </motion.div>

          <motion.div
            className="contact-info"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="info-card">
              <h3>New Projects</h3>
              <p>Have an idea for a website, app, or digital product? Let's discuss how we can bring it to life.</p>
            </div>
            <div className="info-card">
              <h3>Collaboration</h3>
              <p>Looking for a development partner? We're open to collaborating on exciting projects.</p>
            </div>
            <div className="info-card">
              <h3>General Inquiries</h3>
              <p>Questions about our work, process, or availability? Drop us a line — we're happy to chat.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
